import {
  mutateTppSnapshot,
  newStoredFilename,
  nowIso,
  readTppSnapshot,
  resetTppDbSession,
  type TppCardRow,
  type TppEventRow,
  type TppSettings,
  type TppSnapshot,
  writeTppSnapshot,
  normalizeTppSnapshot,
} from './tppLocalDb';
import { resolveTppProofMime } from './tppProofFile';
import { buildTppWorkbookBlob } from './tppZipExcel';

export class TppLocalError extends Error {
  code: string;
  statusCode: number;
  constructor(code: string, statusCode = 409) {
    super(code);
    this.code = code;
    this.statusCode = statusCode;
  }
}

type TppEventSortField =
  | 'id'
  | 'eventKind'
  | 'outDate'
  | 'inDate'
  | 'borrowerName'
  | 'purpose'
  | 'route'
  | 'amount'
  | 'checkoutStatus';

const MAX_CARD_PAGE = 200;
const MAX_EVENT_PAGE = 200;

function sanitizeSearch(q: string): string {
  return q.replace(/[%_\\]/g, '').trim();
}

function parseDateOnly(s: string): string {
  const t = s.trim();
  const datePart = /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : t.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (!datePart) throw new TppLocalError('invalid_date', 400);
  const d = new Date(`${datePart}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) throw new TppLocalError('invalid_date', 400);
  return datePart;
}

function cardAmountSum(cardId: number, events: TppEventRow[]): number {
  let sum = 0;
  for (const e of events) {
    if (e.cardId === cardId && e.amount != null) sum += e.amount;
  }
  return sum;
}

function cardBalance(cardId: number, events: TppEventRow[]): number {
  const bal = Math.round(-cardAmountSum(cardId, events) * 100) / 100;
  return bal === 0 ? 0 : bal;
}

function assertDisplayedBalanceNonNegative(amountSum: number): void {
  if (-amountSum < 0) {
    throw new TppLocalError('insufficient_balance', 400);
  }
}

function assertBalanceAfterReplace(
  cardId: number,
  events: TppEventRow[],
  previousAmount: number | null | undefined,
  newAmount: number | null | undefined
): void {
  const total = cardAmountSum(cardId, events);
  const prev = previousAmount ?? 0;
  const next = newAmount ?? 0;
  assertDisplayedBalanceNonNegative(total - prev + next);
}

function assertBalanceAfterNew(cardId: number, events: TppEventRow[], eventAmount: number): void {
  assertDisplayedBalanceNonNegative(cardAmountSum(cardId, events) + eventAmount);
}

function assertEventAmountMatchesKind(
  eventKind: 'usage' | 'charge',
  amount: number | null | undefined
): void {
  if (amount == null) return;
  if (!Number.isFinite(amount)) {
    throw new TppLocalError('invalid_amount', 400);
  }
  if (eventKind === 'usage' && amount < 0) {
    throw new TppLocalError('amount_non_negative', 400);
  }
  if (eventKind === 'charge' && amount > 0) {
    throw new TppLocalError('charge_amount_non_positive', 400);
  }
}

function getLatestEvent(cardId: number, events: TppEventRow[]): TppEventRow | null {
  let latest: TppEventRow | null = null;
  for (const e of events) {
    if (e.cardId !== cardId) continue;
    if (!latest || e.id > latest.id) latest = e;
  }
  return latest;
}

function findOpenCheckout(cardId: number, events: TppEventRow[], exceptEventId?: number): TppEventRow | null {
  let found: TppEventRow | null = null;
  for (const e of events) {
    if (e.cardId !== cardId) continue;
    if (exceptEventId != null && e.id === exceptEventId) continue;
    // checkoutStatus 優先。欠けたデータは「利用かつ未返却日」を貸出中とみなす
    const open =
      e.checkoutStatus === 1 ||
      (e.eventKind === 'usage' && (e.inDate == null || String(e.inDate).trim() === ''));
    if (!open) continue;
    if (!found || e.id > found.id) found = e;
  }
  return found;
}

function assertNoOpenCheckout(cardId: number, events: TppEventRow[], exceptEventId?: number): void {
  if (findOpenCheckout(cardId, events, exceptEventId)) {
    throw new TppLocalError('already_checked_out', 400);
  }
}

function assertIsLatestEventOnCard(cardId: number, eventId: number, events: TppEventRow[]): void {
  const latest = getLatestEvent(cardId, events);
  if (!latest || latest.id !== eventId) {
    throw new TppLocalError('checkout_status_latest_only', 400);
  }
}

function assertCardInPool(
  snap: TppSnapshot,
  cardId: number,
  poolKey: string
): TppCardRow {
  const card = snap.cards.find((c) => c.id === cardId);
  if (!card || !card.isActive || card.poolKey !== poolKey) {
    throw new TppLocalError('card_not_found', 404);
  }
  return card;
}

function assertDuplicateDisplayNumber(
  snap: TppSnapshot,
  poolKey: string,
  displayNumber: number,
  exceptId?: number
): void {
  const dup = snap.cards.find(
    (c) => c.poolKey === poolKey && c.displayNumber === displayNumber && c.id !== exceptId
  );
  if (dup) throw new TppLocalError('duplicate_display_number', 409);
}

function storeFile(
  snap: TppSnapshot,
  buffer: ArrayBuffer,
  filename: string,
  mimeType: string
): string {
  const storedFilename = newStoredFilename(filename);
  snap.files[storedFilename] = { mimeType, byteSize: buffer.byteLength, data: buffer };
  return storedFilename;
}

function deleteFile(snap: TppSnapshot, storedFilename: string | null | undefined): void {
  if (storedFilename) delete snap.files[storedFilename];
}

function hydrateCardSummary(card: TppCardRow, events: TppEventRow[]) {
  const bal = cardBalance(card.id, events);
  /** チャージ等で新しい行が増えても、未返却の貸出行が残っていれば貸出中 */
  const open = findOpenCheckout(card.id, events);
  const isCheckedOut = open != null;
  return {
    id: card.id,
    poolKey: card.poolKey,
    displayNumber: card.displayNumber,
    physicalId: card.physicalId,
    isActive: card.isActive,
    balance: String(bal),
    balanceNumber: bal,
    isCheckedOut,
    openEventId: isCheckedOut ? open!.id : null,
    openEventRoute: isCheckedOut ? open!.route ?? '' : null,
  };
}

function mapEventSummary(e: TppEventRow) {
  return {
    id: e.id,
    cardId: e.cardId,
    eventKind: e.eventKind,
    outDate: e.outDate,
    inDate: e.inDate,
    departmentLabel: e.departmentLabel,
    borrowerName: e.borrowerName,
    purpose: e.purpose,
    place: e.place,
    route: e.route,
    hasProof: Boolean(e.proofStoredFilename),
    amountNumber: e.amount,
    checkoutStatus: e.checkoutStatus,
  };
}

function eventMatchesSearch(e: TppEventRow, q: string): boolean {
  if (!q) return true;
  const hay = [
    e.borrowerName,
    e.purpose,
    e.place,
    e.route,
    e.departmentLabel,
    e.eventKind,
    String(e.id),
    e.amount != null ? String(e.amount) : '',
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

function cardMatchesSearch(card: TppCardRow, q: string): boolean {
  if (!q) return true;
  if (card.physicalId.toLowerCase().includes(q.toLowerCase())) return true;
  if (/^\d+$/.test(q) && card.displayNumber === parseInt(q, 10)) return true;
  return false;
}

function sortEvents(items: TppEventRow[], sortBy: TppEventSortField, sortDir: 'asc' | 'desc'): TppEventRow[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const tie = b.id - a.id;
    switch (sortBy) {
      case 'id':
        return (a.id - b.id) * dir || tie;
      case 'eventKind':
        return a.eventKind.localeCompare(b.eventKind) * dir || tie;
      case 'outDate':
        return a.outDate.localeCompare(b.outDate) * dir || tie;
      case 'inDate':
        return (a.inDate ?? '').localeCompare(b.inDate ?? '') * dir || tie;
      case 'borrowerName':
        return a.borrowerName.localeCompare(b.borrowerName) * dir || tie;
      case 'purpose':
        return a.purpose.localeCompare(b.purpose) * dir || tie;
      case 'route':
        return a.route.localeCompare(b.route) * dir || tie;
      case 'amount':
        return ((a.amount ?? 0) - (b.amount ?? 0)) * dir || tie;
      case 'checkoutStatus':
        return (a.checkoutStatus - b.checkoutStatus) * dir || tie;
      default:
        return b.outDate.localeCompare(a.outDate) || tie;
    }
  });
}

export const tppLocalStore = {
  async replaceSnapshot(snapshot: TppSnapshot): Promise<void> {
    resetTppDbSession();
    // 読込は変更ではない（revision bump / sync フックなし）
    await writeTppSnapshot(normalizeTppSnapshot(snapshot), false, { notifySync: false });
  },

  async exportSnapshot(): Promise<TppSnapshot> {
    return readTppSnapshot();
  },

  async accessSummary() {
    const snap = await readTppSnapshot();
    const poolKey = snap.settings.poolKey;
    const totalPoolCards = snap.cards.filter((c) => c.poolKey === poolKey).length;
    return {
      totalPoolCards,
      poolKey,
    };
  },

  async getDashboardStats(poolKey: string, opts: { activeOnly: boolean }) {
    const snap = await readTppSnapshot();
    let cards = snap.cards.filter((c) => c.poolKey === poolKey);
    if (opts.activeOnly) cards = cards.filter((c) => c.isActive);
    let checkedOutCards = 0;
    let availableCards = 0;
    let needsChargeCards = 0;
    for (const c of cards) {
      if (findOpenCheckout(c.id, snap.events)) {
        checkedOutCards += 1;
        continue;
      }
      // 「貸出可能」= いま貸出ボタンが出せる枚数（有効・未貸出・残高あり）
      if (c.isActive && cardBalance(c.id, snap.events) > 0) {
        availableCards += 1;
      } else if (c.isActive) {
        needsChargeCards += 1;
      }
    }
    return { totalCards: cards.length, checkedOutCards, availableCards, needsChargeCards };
  },

  async getPoolSettings(poolKey: string): Promise<TppSettings> {
    const snap = await readTppSnapshot();
    if (snap.settings.poolKey !== poolKey) {
      return {
        poolKey,
        displayPrefix: '',
        displaySuffix: '円',
        isoCurrencyCode: null,
        fractionDigits: 0,
        inputRoundingMode: 'none',
        displayRoundingMode: 'none',
      };
    }
    return { ...snap.settings };
  },

  async updatePoolSettings(
    poolKey: string,
    input: Partial<
      Pick<
        TppSettings,
        | 'displayPrefix'
        | 'displaySuffix'
        | 'isoCurrencyCode'
        | 'fractionDigits'
        | 'inputRoundingMode'
        | 'displayRoundingMode'
      >
    >
  ): Promise<TppSettings> {
    const after = await mutateTppSnapshot((snap) => {
      snap.settings.poolKey = poolKey;
      if (input.displayPrefix !== undefined) snap.settings.displayPrefix = input.displayPrefix;
      if (input.displaySuffix !== undefined) snap.settings.displaySuffix = input.displaySuffix;
      if (input.isoCurrencyCode !== undefined) snap.settings.isoCurrencyCode = input.isoCurrencyCode;
      if (input.fractionDigits !== undefined) snap.settings.fractionDigits = input.fractionDigits;
      if (input.inputRoundingMode !== undefined) snap.settings.inputRoundingMode = input.inputRoundingMode;
      if (input.displayRoundingMode !== undefined) snap.settings.displayRoundingMode = input.displayRoundingMode;
    });
    return { ...after.settings };
  },

  async listCardsPage(
    poolKey: string,
    opts: {
      activeOnly: boolean;
      page: number;
      pageSize: number;
      search?: string;
      checkedOutOnly?: boolean;
    }
  ) {
    const snap = await readTppSnapshot();
    const page = Math.max(1, opts.page);
    const pageSize = Math.min(Math.max(1, opts.pageSize), MAX_CARD_PAGE);
    const q = sanitizeSearch(opts.search ?? '');

    let rows = snap.cards.filter((c) => c.poolKey === poolKey);
    if (opts.activeOnly) rows = rows.filter((c) => c.isActive);
    if (q) rows = rows.filter((c) => cardMatchesSearch(c, q));
    if (opts.checkedOutOnly) {
      rows = rows.filter((c) => findOpenCheckout(c.id, snap.events) != null);
    }

    rows.sort((a, b) => a.displayNumber - b.displayNumber || a.id - b.id);

    const total = rows.length;
    const skip = (page - 1) * pageSize;
    const pageRows = rows.slice(skip, skip + pageSize);
    const items = pageRows.map((c) => hydrateCardSummary(c, snap.events));
    return { items, total, page, pageSize };
  },

  async listEventsPage(
    cardId: number,
    poolKey: string,
    opts: {
      page: number;
      pageSize: number;
      search?: string;
      sortBy?: TppEventSortField;
      sortDir?: 'asc' | 'desc';
    }
  ) {
    const snap = await readTppSnapshot();
    const card = snap.cards.find((c) => c.id === cardId);
    if (!card || card.poolKey !== poolKey) throw new TppLocalError('card_not_found', 404);

    const page = Math.max(1, opts.page);
    const pageSize = Math.min(Math.max(1, opts.pageSize), MAX_EVENT_PAGE);
    const q = sanitizeSearch(opts.search ?? '').toLowerCase();

    let rows = snap.events.filter((e) => e.cardId === cardId && eventMatchesSearch(e, q));
    rows = sortEvents(rows, opts.sortBy ?? 'outDate', opts.sortDir ?? 'desc');

    const total = rows.length;
    const skip = (page - 1) * pageSize;
    const items = rows.slice(skip, skip + pageSize).map(mapEventSummary);
    return { items, total, page, pageSize };
  },

  async createCard(data: { poolKey: string; displayNumber: number; physicalId: string }) {
    assertDuplicateDisplayNumber(await readTppSnapshot(), data.poolKey, data.displayNumber);
    let createdId = 0;
    const after = await mutateTppSnapshot((snap) => {
      assertDuplicateDisplayNumber(snap, data.poolKey, data.displayNumber);
      createdId = snap.counters.card++;
      const ts = nowIso();
      snap.cards.push({
        id: createdId,
        poolKey: data.poolKey,
        displayNumber: data.displayNumber,
        physicalId: data.physicalId.trim(),
        isActive: true,
        createdAt: ts,
        updatedAt: ts,
      });
    });
    const card = after.cards.find((c) => c.id === createdId)!;
    return {
      id: card.id,
      poolKey: card.poolKey,
      displayNumber: card.displayNumber,
      physicalId: card.physicalId,
      isActive: card.isActive,
    };
  },

  async updateCard(cardId: number, data: { physicalId?: string; isActive?: boolean }) {
    const before = await readTppSnapshot();
    const card = before.cards.find((c) => c.id === cardId);
    if (!card) throw new TppLocalError('card_not_found', 404);
    if (data.isActive === false) {
      assertNoOpenCheckout(cardId, before.events);
    }
    await mutateTppSnapshot((snap) => {
      const row = snap.cards.find((c) => c.id === cardId);
      if (!row) return;
      if (data.physicalId != null) row.physicalId = data.physicalId.trim();
      if (data.isActive != null) row.isActive = data.isActive;
      row.updatedAt = nowIso();
    });
    const after = await readTppSnapshot();
    return after.cards.find((c) => c.id === cardId)!;
  },

  async checkout(
    cardId: number,
    poolKey: string,
    body: {
      outDate: string;
      purpose: string;
      place: string;
      route: string;
      departmentLabel?: string;
      borrowerName?: string;
    }
  ) {
    const outDate = parseDateOnly(body.outDate);
    let eventId = 0;
    await mutateTppSnapshot((snap) => {
      assertCardInPool(snap, cardId, poolKey);
      assertNoOpenCheckout(cardId, snap.events);
      eventId = snap.counters.event++;
      const ts = nowIso();
      snap.events.push({
        id: eventId,
        cardId,
        eventKind: 'usage',
        outDate,
        inDate: null,
        departmentLabel: body.departmentLabel?.trim() ?? '',
        borrowerName: body.borrowerName?.trim() ?? '',
        purpose: body.purpose,
        place: body.place,
        route: body.route,
        amount: null,
        checkoutStatus: 1,
        proofStoredFilename: null,
        proofMime: null,
        createdAt: ts,
        updatedAt: ts,
      });
    });
    const snap = await readTppSnapshot();
    return snap.events.find((e) => e.id === eventId)!;
  },

  async checkin(
    eventId: number,
    body: { inDate: string; amount: number; route: string; proofFile?: File }
  ) {
    const inDate = parseDateOnly(body.inDate);
    if (body.amount < 0) throw new TppLocalError('amount_non_negative', 400);

    const before = await readTppSnapshot();
    const ev = before.events.find((e) => e.id === eventId);
    if (!ev) throw new TppLocalError('event_not_found', 404);
    if (ev.checkoutStatus !== 1) throw new TppLocalError('not_checkout_for_return', 400);
    assertBalanceAfterReplace(ev.cardId, before.events, ev.amount, body.amount);

    let proofBuffer: ArrayBuffer | null = null;
    let proofName = 'proof.jpg';
    let proofMime = 'image/jpeg';
    if (body.proofFile?.size) {
      proofBuffer = await body.proofFile.arrayBuffer();
      proofName = body.proofFile.name;
      proofMime = resolveTppProofMime(body.proofFile.type, body.proofFile.name);
    }

    await mutateTppSnapshot((snap) => {
      const row = snap.events.find((e) => e.id === eventId);
      if (!row || row.checkoutStatus !== 1) throw new TppLocalError('not_checkout_for_return', 400);
      assertBalanceAfterReplace(row.cardId, snap.events, row.amount, body.amount);

      const oldKey = row.proofStoredFilename;
      if (proofBuffer) {
        const stored = storeFile(snap, proofBuffer, proofName, proofMime);
        if (oldKey && oldKey !== stored) deleteFile(snap, oldKey);
        row.proofStoredFilename = stored;
        row.proofMime = proofMime;
      }

      row.inDate = inDate;
      row.amount = body.amount;
      row.route = body.route;
      row.checkoutStatus = 0;
      row.updatedAt = nowIso();
    });

    const snap = await readTppSnapshot();
    return snap.events.find((e) => e.id === eventId)!;
  },

  async charge(
    cardId: number,
    poolKey: string,
    body: { outDate: string; amount: number; note?: string }
  ) {
    const outDate = parseDateOnly(body.outDate);
    const neg = body.amount === 0 ? 0 : -Math.abs(body.amount);
    const purpose = body.note?.trim() ?? '';

    let eventId = 0;
    await mutateTppSnapshot((snap) => {
      assertCardInPool(snap, cardId, poolKey);
      assertBalanceAfterNew(cardId, snap.events, neg);
      eventId = snap.counters.event++;
      const ts = nowIso();
      snap.events.push({
        id: eventId,
        cardId,
        eventKind: 'charge',
        outDate,
        inDate: outDate,
        departmentLabel: '',
        borrowerName: '',
        purpose,
        place: '',
        route: '',
        amount: neg,
        checkoutStatus: 0,
        proofStoredFilename: null,
        proofMime: null,
        createdAt: ts,
        updatedAt: ts,
      });
    });
    const snap = await readTppSnapshot();
    return snap.events.find((e) => e.id === eventId)!;
  },

  async updateEvent(
    eventId: number,
    data: {
      outDate?: string;
      inDate?: string | null;
      departmentLabel?: string;
      borrowerName?: string;
      purpose?: string;
      place?: string;
      route?: string;
      amount?: number | null;
      checkoutStatus?: 0 | 1;
      eventKind?: 'usage' | 'charge';
    }
  ) {
    const before = await readTppSnapshot();
    const ev = before.events.find((e) => e.id === eventId);
    if (!ev) throw new TppLocalError('event_not_found', 404);

    const nextEventKind = data.eventKind ?? ev.eventKind;
    if (data.checkoutStatus === 1) {
      if (nextEventKind !== 'usage') throw new TppLocalError('checkout_usage_only', 400);
      assertIsLatestEventOnCard(ev.cardId, eventId, before.events);
      if (ev.checkoutStatus !== 1) assertNoOpenCheckout(ev.cardId, before.events);
    }

    let amountNext: number | null | undefined;
    if (data.amount !== undefined) {
      amountNext = data.amount;
      // チャージは UI が正数で送っても負数に正規化（charge() と同じ）
      if (amountNext != null && nextEventKind === 'charge' && amountNext !== 0) {
        amountNext = -Math.abs(amountNext);
      }
      assertEventAmountMatchesKind(nextEventKind, amountNext);
    } else if (data.eventKind != null && data.eventKind !== ev.eventKind) {
      assertEventAmountMatchesKind(nextEventKind, ev.amount);
    }

    if (amountNext !== undefined) {
      assertBalanceAfterReplace(ev.cardId, before.events, ev.amount, amountNext ?? null);
    }

    await mutateTppSnapshot((snap) => {
      const row = snap.events.find((e) => e.id === eventId);
      if (!row) throw new TppLocalError('event_not_found', 404);

      if (data.checkoutStatus === 1 && row.checkoutStatus !== 1) {
        assertNoOpenCheckout(row.cardId, snap.events);
      }

      const nextKind = data.eventKind ?? row.eventKind;
      const nextAmount = amountNext !== undefined ? amountNext : row.amount;
      if (amountNext !== undefined) {
        assertBalanceAfterReplace(row.cardId, snap.events, row.amount, nextAmount ?? null);
      }

      if (data.outDate != null) row.outDate = parseDateOnly(data.outDate);
      if (data.inDate !== undefined) row.inDate = data.inDate == null ? null : parseDateOnly(data.inDate);
      if (data.departmentLabel !== undefined) row.departmentLabel = data.departmentLabel;
      if (data.borrowerName !== undefined) row.borrowerName = data.borrowerName;
      if (data.purpose != null) row.purpose = data.purpose;
      if (data.place != null) row.place = data.place;
      if (data.route != null) row.route = data.route;
      if (amountNext !== undefined) row.amount = amountNext;
      if (data.checkoutStatus != null) row.checkoutStatus = data.checkoutStatus;
      if (data.eventKind != null) row.eventKind = data.eventKind;
      row.updatedAt = nowIso();
    });

    const snap = await readTppSnapshot();
    return snap.events.find((e) => e.id === eventId)!;
  },

  async getEventProofBlob(eventId: number): Promise<Blob> {
    const snap = await readTppSnapshot();
    const ev = snap.events.find((e) => e.id === eventId);
    if (!ev) throw new TppLocalError('event_not_found', 404);
    const key = ev.proofStoredFilename?.trim();
    if (!key) throw new TppLocalError('proof_not_found', 404);
    const file = snap.files[key];
    if (!file) throw new TppLocalError('proof_file_read_failed', 404);
    return new Blob([file.data], { type: ev.proofMime ?? file.mimeType });
  },

  async uploadEventProof(eventId: number, file: File) {
    const before = await readTppSnapshot();
    const ev = before.events.find((e) => e.id === eventId);
    if (!ev) throw new TppLocalError('event_not_found', 404);

    const buf = await file.arrayBuffer();
    const mime = resolveTppProofMime(file.type, file.name);

    await mutateTppSnapshot((snap) => {
      const row = snap.events.find((e) => e.id === eventId);
      if (!row) throw new TppLocalError('event_not_found', 404);
      const oldKey = row.proofStoredFilename;
      const stored = storeFile(snap, buf, file.name, mime);
      if (oldKey && oldKey !== stored) deleteFile(snap, oldKey);
      row.proofStoredFilename = stored;
      row.proofMime = mime;
      row.updatedAt = nowIso();
    });

    const snap = await readTppSnapshot();
    return snap.events.find((e) => e.id === eventId)!;
  },

  async importCardsFromRows(
    rows: Array<{ poolKey?: string; displayNumber: number; physicalId: string; isActive?: boolean }>
  ): Promise<{ created: number; updated: number }> {
    let created = 0;
    let updated = 0;
    await mutateTppSnapshot((snap) => {
      for (const r of rows) {
        const poolKey = r.poolKey ?? snap.settings.poolKey ?? 'default';
        const isActive = r.isActive ?? true;
        const existing = snap.cards.find((c) => c.poolKey === poolKey && c.displayNumber === r.displayNumber);
        if (existing) {
          existing.physicalId = r.physicalId.trim();
          existing.isActive = isActive;
          existing.updatedAt = nowIso();
          updated += 1;
        } else {
          const id = snap.counters.card++;
          const ts = nowIso();
          snap.cards.push({
            id,
            poolKey,
            displayNumber: r.displayNumber,
            physicalId: r.physicalId.trim(),
            isActive,
            createdAt: ts,
            updatedAt: ts,
          });
          created += 1;
        }
      }
    });
    return { created, updated };
  },

  async buildExportWorkbookBlob(opts?: {
    poolKey?: string;
    cardId?: number;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Blob> {
    const snap = await readTppSnapshot();
    const poolKey = opts?.poolKey ?? snap.settings.poolKey;
    let cards = snap.cards.filter((c) => c.poolKey === poolKey);
    let events = snap.events.filter((e) => cards.some((c) => c.id === e.cardId));

    if (opts?.cardId != null) {
      cards = cards.filter((c) => c.id === opts.cardId);
      events = events.filter((e) => e.cardId === opts.cardId);
    }
    if (opts?.dateFrom) {
      const from = parseDateOnly(opts.dateFrom);
      events = events.filter((e) => e.outDate >= from);
    }
    if (opts?.dateTo) {
      const to = parseDateOnly(opts.dateTo);
      events = events.filter((e) => e.outDate <= to);
    }

    const filtered: TppSnapshot = {
      ...snap,
      settings: { ...snap.settings, poolKey },
      cards,
      events,
    };
    return buildTppWorkbookBlob(filtered);
  },
};
